import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle, Color } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { TableKit } from "@tiptap/extension-table";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import type { EditorView } from "@tiptap/pm/view";
import MediaPicker from "../Media/MediaPicker";
import { registerPendingImage } from "./pending-images";
import { RichTextToolbar } from "./RichTextToolbar";

type MediaFolder = "brands" | "blog" | "content" | "social" | "general";

// Panodaki/sürüklenen dosyalardan görselleri editöre ekler. Görsel yoksa
// false döner; o zaman ProseMirror kendi varsayılan davranışını sürdürür.
function insertImageFiles(view: EditorView, files: File[], event: Event): boolean {
  const images = files.filter((f) => f.type.startsWith("image/"));
  if (images.length === 0) return false;

  event.preventDefault();
  const imageNode = view.state.schema.nodes.image;
  if (!imageNode) return false;

  const tr = view.state.tr;
  for (const file of images) {
    tr.replaceSelectionWith(imageNode.create({ src: registerPendingImage(file) }));
  }
  view.dispatch(tr);
  return true;
}

export function RichTextEditor({
  value,
  onChange,
  allowImages = false,
  imageFolder = "content",
  id,
}: {
  value: string;
  onChange: (html: string) => void;
  // Yazının içine (imlecin olduğu yere) görsel eklemeyi açar.
  allowImages?: boolean;
  // MediaPicker'ın varsayılan klasörü — hem filtre hem yükleme hedefi.
  imageFolder?: MediaFolder;
  // Kapsayıcının DOM id'si — form doğrulaması alana odaklanabilsin diye.
  id?: string;
}) {
  const [mediaOpen, setMediaOpen] = useState(false);
  const editor = useEditor({
    extensions: [
      // TipTap 3 StarterKit başlık (1–6), altı/üstü çizili, kod, kod bloğu, alıntı,
      // listeler, yatay çizgi, link ve geri al/yinele'yi içerir.
      StarterKit.configure({
        link: {
          // Editörde linke tıklamak sayfadan çıkarıp kaydedilmemiş içeriği kaybettirmesin.
          openOnClick: false,
          // Varsayılan her linki yeni sekmeye (target=_blank) açıyordu; bunu
          // link penceresindeki "Yeni sekmede aç" seçimi belirlesin.
          HTMLAttributes: { target: null, rel: null },
          defaultProtocol: "https",
        },
      }),
      // inline:false → görsel kendi bloğunda durur, paragrafın içine sıkışmaz.
      Image.configure({ inline: false, allowBase64: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      // Yalnızca renk: TextStyleKit'teki yazı tipi/boyut stilleri API'de silinir,
      // editör ile site farklı görünürdü.
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Subscript,
      Superscript,
      // Sütun genişliği sürükleme kapalı: genişlikler style ile yazılıyor ve
      // kayıtta (sanitize) siliniyor.
      TableKit.configure({ table: { resizable: false } }),
    ],
    content: value || "",
    immediatelyRender: false,
    editorProps: allowImages
      ? {
          // Panodan yapıştırılan / sürüklenen görseller: dosya hemen yüklenmez,
          // geçici blob: önizlemesiyle editöre konur. Yükleme "Kaydet"te olur
          // (bkz. pending-images.ts).
          handlePaste: (view, event) =>
            insertImageFiles(view, Array.from(event.clipboardData?.files ?? []), event),
          handleDrop: (view, event) =>
            insertImageFiles(view, Array.from(event.dataTransfer?.files ?? []), event),
        }
      : // TipTap 3 editorProps'u varsayılan {} ile birleştirmez; undefined verilirse
        // editör açılırken "reading 'dispatchTransaction'" hatasıyla çöker.
        {},
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Dışarıdan gelen değer (namespace/locale değişimi) editörü senkronlar.
  // emitUpdate:false ile bu programatik set, dirty/onChange tetiklemez.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div id={id} className="border border-input rounded-md overflow-hidden">
      <RichTextToolbar editor={editor} onInsertImage={allowImages ? () => setMediaOpen(true) : undefined} />

      {/* Editor area */}
      <EditorContent
        editor={editor}
        className={cn(
          "p-3 min-h-[160px] overflow-x-auto [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[136px]",
          "[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5",
          "[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5",
          "[&_.ProseMirror_li>p]:my-0",
          "[&_.ProseMirror_p]:my-0",
          // Başlıklar — sitedeki .rich-text tipografisine yakın (WYSIWYG)
          "[&_.ProseMirror_:is(h1,h2,h3,h4,h5,h6)]:font-bold [&_.ProseMirror_:is(h1,h2,h3,h4,h5,h6)]:leading-tight",
          "[&_.ProseMirror_:is(h1,h2,h3,h4,h5,h6)]:mt-4 [&_.ProseMirror_:is(h1,h2,h3,h4,h5,h6)]:mb-2",
          "[&_.ProseMirror_:is(h1,h2,h3,h4,h5,h6):first-child]:mt-0",
          "[&_.ProseMirror_h1]:text-3xl [&_.ProseMirror_h1]:font-extrabold [&_.ProseMirror_h2]:text-2xl",
          "[&_.ProseMirror_h3]:text-xl [&_.ProseMirror_h4]:text-lg [&_.ProseMirror_h5]:text-base",
          "[&_.ProseMirror_h6]:text-sm [&_.ProseMirror_h6]:uppercase [&_.ProseMirror_h6]:tracking-wide",
          // Alıntı, kod, çizgi, link, vurgu
          "[&_.ProseMirror_blockquote]:my-3 [&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-primary/40 [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:italic [&_.ProseMirror_blockquote]:text-muted-foreground",
          "[&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:bg-muted [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:py-0.5 [&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-[0.875em]",
          "[&_.ProseMirror_pre]:my-3 [&_.ProseMirror_pre]:overflow-x-auto [&_.ProseMirror_pre]:rounded-md [&_.ProseMirror_pre]:border [&_.ProseMirror_pre]:border-border [&_.ProseMirror_pre]:bg-muted [&_.ProseMirror_pre]:p-3",
          "[&_.ProseMirror_pre_code]:bg-transparent [&_.ProseMirror_pre_code]:p-0",
          "[&_.ProseMirror_hr]:my-4 [&_.ProseMirror_hr]:border-border [&_.ProseMirror_hr.ProseMirror-selectednode]:border-primary",
          "[&_.ProseMirror_a]:text-primary [&_.ProseMirror_a]:underline [&_.ProseMirror_a]:underline-offset-2",
          "[&_.ProseMirror_mark]:rounded-sm [&_.ProseMirror_mark]:px-0.5",
          // Tablolar: kenarlıklı hücreler, seçili hücreler belirgin
          "[&_.ProseMirror_table]:my-3 [&_.ProseMirror_table]:w-full [&_.ProseMirror_table]:border-collapse [&_.ProseMirror_table]:table-fixed",
          "[&_.ProseMirror_:is(th,td)]:relative [&_.ProseMirror_:is(th,td)]:border [&_.ProseMirror_:is(th,td)]:border-border [&_.ProseMirror_:is(th,td)]:px-2 [&_.ProseMirror_:is(th,td)]:py-1 [&_.ProseMirror_:is(th,td)]:align-top [&_.ProseMirror_:is(th,td)]:text-left",
          "[&_.ProseMirror_th]:bg-muted [&_.ProseMirror_th]:font-semibold",
          "[&_.ProseMirror_.selectedCell]:bg-primary/10",
          "[&_.ProseMirror_.tableWrapper]:overflow-x-auto",
          "[&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:h-auto [&_.ProseMirror_img]:my-3",
          "[&_.ProseMirror_img.ProseMirror-selectednode]:outline [&_.ProseMirror_img.ProseMirror-selectednode]:outline-2 [&_.ProseMirror_img.ProseMirror-selectednode]:outline-primary",
        )}
      />

      {allowImages && (
        <MediaPicker
          defaultFolder={imageFolder}
          open={mediaOpen}
          onClose={() => setMediaOpen(false)}
          onSelect={(url) => {
            editor.chain().focus().setImage({ src: url }).run();
            setMediaOpen(false);
          }}
        />
      )}
    </div>
  );
}
